import { useTranslation } from 'react-i18next';
import { Outlet, Route, Routes, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import CircularProgress from '@mui/material/CircularProgress';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import FlightIcon from '@mui/icons-material/Flight';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  LoginPage,
  RegisterPage,
  MfaVerifyPage,
  MfaSetupPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  OAuthCallbackPage,
  ProtectedRoute,
  useAuth,
} from '@/features/auth';
import {
  TripListPage,
  CreateTripPage,
  EditTripPage,
  TripDetailPage,
} from '@/features/trips';
import { InteractiveMap } from '@/features/map';
import { FeedPage } from '@/features/social';
import { ProfilePage, SettingsPage } from '@/features/profile';
import { SearchResultsPage } from '@/features/search';
import { ReferralDashboardPage, PlanUpgradePage } from '@/features/referral';
import { AdminPage } from '@/features/admin';
import { GalleryPage, PublicGalleryPage } from '@/features/gallery';
import { LandingPage } from '@/pages/LandingPage';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { MyGlobePage } from '@/pages/MyGlobePage';

function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const getNavValue = () => {
    if (location.pathname === '/' || location.pathname.startsWith('/map')) return 0;
    if (location.pathname.startsWith('/globe')) return 1;
    if (location.pathname.startsWith('/trips')) return 2;
    if (location.pathname.startsWith('/feed')) return 3;
    if (location.pathname.startsWith('/profile')) return 4;
    if (location.pathname === '/porta-retratos') return 5;
    return 0;
  };

  return (
    <>
      <AppBar position="fixed" color="primary" elevation={1}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <svg viewBox="0 0 60 72" width="28" height="34" xmlns="http://www.w3.org/2000/svg">
              <defs><linearGradient id="navLogo" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#42a5f5"/><stop offset="100%" stopColor="#0d47a1"/></linearGradient></defs>
              <path d="M30 68 C30 68 56 44 56 28 C56 13.6 44.4 2 30 2 C15.6 2 4 13.6 4 28 C4 44 30 68 30 68Z" fill="url(#navLogo)"/>
              <circle cx="30" cy="26" r="14" fill="white"/>
              <circle cx="30" cy="26" r="13" fill="none" stroke="#1565c0" strokeWidth="1.4"/>
              <ellipse cx="30" cy="26" rx="5.5" ry="13" fill="none" stroke="#1565c0" strokeWidth="1"/>
              <line x1="17" y1="26" x2="43" y2="26" stroke="#1565c0" strokeWidth="1"/>
            </svg>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
              Globe Pinned
            </Typography>
          </Box>
          <LanguageSwitcher />
          <IconButton color="inherit" onClick={() => { logout(); navigate('/auth/login'); }} title="Sair">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <BottomNavigation
        value={getNavValue()}
        onChange={(_, newValue) => {
          const routes = ['/', '/globe', '/trips', '/feed', '/profile', '/porta-retratos'];
          navigate(routes[newValue]);
        }}
        showLabels
        sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200, boxShadow: '0 -2px 8px rgba(0,0,0,0.1)' }}
      >
        <BottomNavigationAction label={t("nav.location")} icon={<LocationOnIcon />} />
        <BottomNavigationAction label={t("nav.myGlobe")} icon={<PublicIcon />} />
        <BottomNavigationAction label={t("nav.trips")} icon={<FlightIcon />} />
        <BottomNavigationAction label={t("nav.feed")} icon={<PeopleIcon />} />
        <BottomNavigationAction label={t("nav.profile")} icon={<PersonIcon />} />
        <BottomNavigationAction label={t("nav.gallery")} icon={<PhotoLibraryIcon />} />
      </BottomNavigation>
    </>
  );
}

function Layout() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Box component="main" sx={{ flex: 1, mt: '64px', mb: '56px' }}>
        <Outlet />
      </Box>
    </Box>
  );
}

function MapPage() {
  return <InteractiveMap />;
}

/**
 * Smart wrapper for the "/" route that checks auth state.
 * - If loading: shows spinner
 * - If not authenticated: shows LandingPage
 * - If authenticated: shows Layout with MapPage
 */
function HomeRouteWrapper() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  // Authenticated: use layout system
  return <Navigate to="/map" replace />;
}

function App() {
  return (
    <Routes>
      {/* Home route: Landing for unauth, Map for auth */}
      <Route path="/" element={<HomeRouteWrapper />} />

      {/* Public landing page (always accessible) */}
      <Route path="/landing" element={<LandingPage />} />

      {/* Public gallery (no auth required) */}
      <Route path="/gallery/:shareToken" element={<PublicGalleryPage />} />

      {/* Auth routes (public) */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/register" element={<RegisterPage />} />
      <Route path="/auth/mfa" element={<MfaVerifyPage />} />
      <Route path="/auth/mfa/setup" element={<MfaSetupPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />

      {/* Protected routes with navbar */}
      <Route element={<Layout />}>
        <Route path="/map" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
        <Route path="/globe" element={<ProtectedRoute><MyGlobePage /></ProtectedRoute>} />
        <Route path="/trips" element={<ProtectedRoute><TripListPage /></ProtectedRoute>} />
        <Route path="/trips/new" element={<ProtectedRoute><CreateTripPage /></ProtectedRoute>} />
        <Route path="/trips/:tripId" element={<ProtectedRoute><TripDetailPage /></ProtectedRoute>} />
        <Route path="/trips/:tripId/edit" element={<ProtectedRoute><EditTripPage /></ProtectedRoute>} />
        <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchResultsPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><ReferralDashboardPage /></ProtectedRoute>} />
        <Route path="/plans" element={<ProtectedRoute><PlanUpgradePage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/porta-retratos" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
}

export default App;